import React, { useState } from "react";
import Box from "@mui/material/Box";
import { Card, CardContent, TextField, Typography, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import { useGlobalMDAlert } from "context";
import { FP, StatCard, StatusPill, fpInputSx } from "components/financepro";
import { useCreds, postDO, SEMAFORO, fmtMoneyDO, IntroPanel } from "./common";

const PASOS = [
  { icon: SearchIcon, t: "Ingresa la cédula", d: "Escribe la cédula del solicitante en el buscador de arriba." },
  { icon: FactCheckRoundedIcon, t: "Revisa el semáforo", d: "Verás el nivel de riesgo y el detalle por empresa (anonimizado)." },
  { icon: VerifiedUserRoundedIcon, t: "Decide con respaldo", d: "Aprueba o rechaza con base en el historial real de toda la red." },
];

// Pestaña "Depurar Cliente": consulta una cédula contra la red.
export function ConsultaCliente() {
  const creds = useCreds();
  const { showAlert } = useGlobalMDAlert();
  const [cedula, setCedula] = useState("");
  const [cargando, setCargando] = useState(false);
  const [res, setRes] = useState(null);

  function consultar() {
    if (!cedula.trim()) {
      showAlert({ message: "Ingrese una cédula", color: "error", autoHideDuration: 3000 });
      return;
    }
    setCargando(true);
    postDO("/api/dataorbit_consultar", {
      usuario: creds.usuario,
      password: creds.password,
      nombrenegocio: creds.nombrenegocio,
      cedula: cedula.trim(),
    })
      .then(({ ok, data }) => {
        if (ok) setRes(data.data);
        else {
          setRes(null);
          showAlert({ message: data.Error || "Error en la consulta", color: "error", autoHideDuration: 3000 });
        }
      })
      .finally(() => setCargando(false));
  }

  const sem = res ? SEMAFORO[res.semaforo] || SEMAFORO.verde : null;

  return (
    <>
      <Card
        elevation={0}
        sx={{ mb: 3, borderRadius: 2.5, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.cardBg, boxShadow: FP.shadow.soft }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: "flex", gap: 1.5, flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "flex-end" } }}>
            <TextField
              label="Cédula a consultar"
              value={cedula}
              onChange={(e) => setCedula(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && consultar()}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
            <Button
              variant="contained"
              disabled={cargando}
              startIcon={<SearchIcon />}
              onClick={consultar}
              sx={{
                minWidth: { sm: 180 },
                height: 46,
                bgcolor: FP.color.navy,
                color: "#FFFFFF",
                borderRadius: 1.4,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
              }}
            >
              {cargando ? "Consultando..." : "Consultar"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {!res && (
        <IntroPanel
          title="Buró de crédito compartido"
          subtitle="Consulta el comportamiento de pago de cualquier cédula en todas las empresas de la red antes de aprobar un préstamo."
          steps={PASOS}
        />
      )}

      {res && (
        <>
          <Card
            elevation={0}
            sx={{ mb: 3, borderRadius: 2.5, border: `1px solid ${sem.color}`, bgcolor: sem.soft, boxShadow: FP.shadow.soft }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                <Box>
                  <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1.2rem" }}>
                    {res.nombre || "Sin nombre registrado"}
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.85rem" }}>
                    Cédula {res.cedula} · {res.encontrado ? `aparece en ${res.num_empresas} empresa(s)` : "sin historial en la red"}
                  </Typography>
                  {/* De cuándo es el dato: un número leído ahora mismo no
                      vale lo mismo que el del último reporte. */}
                  <Typography sx={{ color: FP.color.textMuted, fontSize: "0.75rem", mt: 0.3 }}>
                    {res.negocios_en_vivo > 0
                      ? `${res.negocios_en_vivo} negocio(s) leído(s) en vivo hace un instante`
                      : "Según el último reporte de cada negocio"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <StatusPill label={sem.label} variant={sem.variant} />
                  {res.incobrable && (
                    <Box sx={{ mt: 0.6 }}>
                      <StatusPill label="Reportado incobrable" variant="danger" />
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {res.encontrado && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  mb: 3,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                }}
              >
                <StatCard label="Empresas" value={String(res.num_empresas)} icon={GroupsRoundedIcon} accent={FP.color.accentBlue} />
                <StatCard label="Atraso máximo" value={`${res.max_dias_atraso} d`} icon={WarningAmberRoundedIcon} accent={sem.color} />
                <StatCard label="Total vencido (red)" value={fmtMoneyDO(res.total_vencido)} icon={PaidRoundedIcon} accent={FP.color.danger} />
              </Box>

              <Card elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.cardBg, boxShadow: FP.shadow.soft, overflow: "hidden" }}>
                <Box sx={{ px: { xs: 2, md: 3 }, py: 1.6, borderBottom: `1px solid ${FP.color.border}`, bgcolor: "#FAFBFE" }}>
                  <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
                    Detalle por empresa
                  </Typography>
                </Box>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 1.4 }}>
                    {res.detalle.map((d, i) => {
                      let nivel = "verde";
                      if (d.dias_atraso > 90) nivel = "rojo";
                      else if (d.dias_atraso > 0) nivel = "amarillo";
                      const ds = SEMAFORO[nivel];
                      return (
                        <Box key={i} sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, p: 1.5, bgcolor: d.propia ? FP.color.accentBlueSoft : "#FFFFFF" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "0.95rem" }}>
                              {d.empresa}
                            </Typography>
                            <StatusPill label={`${d.dias_atraso} d`} variant={ds.variant} />
                          </Box>
                          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1 }}>
                            {[
                              ["Monto", fmtMoneyDO(d.monto)],
                              ["Estado", d.estado || "—"],
                              ["Cuotas venc.", String(d.cuotas_vencidas)],
                              ["Vencido", fmtMoneyDO(d.vencido)],
                            ].map(([l, v]) => (
                              <Box key={l}>
                                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>{l}</Typography>
                                <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.9rem", fontWeight: 600 }}>{v}</Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </>
  );
}
